/** Sprint 5 · Form Builder · 主管審查佇列（pending_review → approve / reject 附備註）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import FunctionPageHeader from '../../../components/FunctionPageHeader';
import { listForms, reviewForm, FormSummary } from './services/formBuilderService';

const FUNC = 'form_manager';

const FormReviewPage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const [items, setItems] = useState<FormSummary[]>([]);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const all = (await listForms()).items;
      setItems(all.filter((f) => f.status === 'pending_review'));
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { if (!permLoading && hasPermission(FUNC, 'read')) load(); }, [permLoading, hasPermission, load]);

  const approve = async (id: number) => {
    try { await reviewForm(id, 'approve'); showSuccess('SYS420001'); load(); } catch (err) { showApiError(err); }
  };
  const doReject = async () => {
    if (rejectId == null) return;
    try { await reviewForm(rejectId, 'reject', note); showSuccess('SYS420001'); setRejectId(null); setNote(''); load(); }
    catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!hasPermission(FUNC, 'read')) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限')}</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <FunctionPageHeader funcCode={FUNC} />
      <Button size="small" sx={{ my: 1 }} onClick={() => nav('/form_manager')}>← {tt('back', '返回列表')}</Button>
      <Typography variant="h6" gutterBottom>{tt('reviewQueue', '待審清單')}</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{tt('title', '標題')}</TableCell>
            <TableCell align="right">{tt('actions', '操作')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={2}>
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{tt('noPending', '目前沒有待審表單。')}</Typography>
            </TableCell></TableRow>}
            {items.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>
                  <Button variant="text" onClick={() => nav(`/form_manager/builder/${f.id}`)}>{f.title}</Button>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="contained" color="success" onClick={() => approve(f.id)}>{tt('approve', '核准')}</Button>
                    <Button size="small" variant="outlined" color="warning" onClick={() => { setRejectId(f.id); setNote(''); }}>{tt('reject', '退回')}</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={rejectId != null} onClose={() => setRejectId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('rejectTitle', '退回原因')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={2} size="small" sx={{ mt: 1 }}
            label={tt('note', '備註')} value={note} onChange={(e) => setNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectId(null)}>{tt('cancel', '取消')}</Button>
          <Button variant="contained" color="warning" disabled={!note.trim()} onClick={doReject}>{tt('reject', '退回')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormReviewPage;
